import { useMemo } from 'react'

const range = (start: number, end: number) => {
  const length = end - start + 1
  return Array.from({ length }, (_, k) => k + start)
}

interface PaginationPropsType {
  totalCount: number
  pageSize: number
  currentPage: number
  siblingCount?: number
  limitPage?: number
}

const usePagination = ({
  totalCount,
  pageSize,
  currentPage,
  siblingCount = 1,
  limitPage = 5
}: PaginationPropsType) => {
  const totalPage = Math.ceil(totalCount / pageSize)

  const paginationRange = useMemo(() => {
    const totalSibling = siblingCount + limitPage

    if (totalPage <= totalSibling) {
      return range(1, totalPage)
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1)
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPage)

    const shouldShowLeftDots = leftSiblingIndex > 2
    const shouldShowRightDots = rightSiblingIndex < totalPage - 2

    const firstPageIndex = 1
    const lastPageIndex = totalPage

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingCount
      const leftRange = range(1, leftItemCount)

      return [...leftRange, NaN, totalPage]
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingCount
      const rightRange = range(totalPage - rightItemCount + 1, totalPage)
      return [firstPageIndex, NaN, ...rightRange]
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex)
      return [firstPageIndex, NaN, ...middleRange, NaN, lastPageIndex]
    }
  }, [totalCount, pageSize, currentPage, siblingCount, limitPage])

  return { paginationRange, totalPage }
}

export default usePagination
